import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';
import { MOTION_GRAPHICS_SCENE_DESIGN_ID } from 'js/src/agent/tools/motion-graphics/motionGraphicsSceneDesign';
import Flags from 'js/src/libs/flags';
import { recipeCopy } from 'js/src/agent/skills/recipeCopy';

export const createKineticTypographyVideoSkill: Skill = {
    name: SKILL_NAMES.createKineticTypographyVideo,
    description:
        'Kinetic typography video — a voiced video made entirely of type, where the spoken ' +
        'phrases build word by word at a readable size and, once a sentence, the word it turns ' +
        'on fills the frame and moves the way it means: "grow" grows, "fall" falls, opposites ' +
        'split the frame in two, a repeated word echoes down the screen. Use for ' +
        'manifesto-style brand pieces, launch hype, bold social videos, and any script whose ' +
        'power is in its words.',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        ...recipeCopy(SKILL_NAMES.createKineticTypographyVideo),
        isAvailable: () => Flags.canUseCodeo(),
    },
    instructions: `# Create Kinetic Typography Video

A video made of words. The voice speaks, and **what it says builds on screen phrase by phrase**,
word by word at a readable size, with one word in it set apart: in the accent, underlined, or
filled with the brand gradient. Then, once in each graphic, **the word
the line turns on fills the frame and acts out what it means**, on a new colour. "Grow" grows.
"Fall" falls and hits the floor. "Again" repeats down the screen. "Light and dark" splits the
frame into its own negative. Nothing else is on screen: no footage, no icons, no charts. The
type is the image, and the motion is the meaning.

Phrases are the plain beats, and the restraint is what makes the moment land: the frame-filling word,
its ground cut, its texture and its treatment all arrive together, once a graphic. A graphic
whose key beat is a phrase instead gives that phrase its hero moment: a word cut out of the
brand gradient, an underline drawing itself, a payoff flying in as a 3D block.

Load the \`${SKILL_NAMES.createVideo}\` skill too. Everything there applies, and this skill's rules
win where they conflict. If the user's material needs to be *seen* (a product, a place, a
face), say so plainly and offer \`${SKILL_NAMES.createVideo}\` instead.

## Structure
- **One AI voiceover scene group for the whole video**
  (\`${SKILL_NAMES.aiVoiceoverVideoBasics}\`), with every visual a graphic over it, title and
  outro included. The format is always voiced: set \`require_voiceover: true\` on the voice
  question.
- Add the graphics with \`${TOOL_NAMES.addVoiceoverScene}\`, \`scene_design_id\`
  \`"${MOTION_GRAPHICS_SCENE_DESIGN_ID}"\`, in ONE call listing every word range in order.
  **Every transcript word is covered** (\`${SKILL_NAMES.voiceoverVideoBasics}\` has the rules).
- **Captions are off**: call ${TOOL_NAMES.setCaptions} with enabled: false right after
  generating the voiceover. The type on screen is the caption.

## Script: written to be acted out
The video can only be as good as its words. Write the script so that most lines carry a word
that can *do* something on screen.
- **Verbs and adjectives of change.** Grow, fall, split, build, repeat, rise, break, gather,
  loop, faster, bigger, opposite. Abstract nouns ("solutions", "synergy") cannot be acted out
  and leave a beat with nothing to do.
- **Short sentences, each turning on one showable word.**
- **Phrases that stand on their own.** Most of the script is shown as the phrase itself:
  two to six words that make sense on screen ("You give it a brief. It gives you back a video.").
- **Runs of single words.** A list said as single words ("It reads. Thinks. Writes. Cuts.")
  is one key beat: the words swap in place in one slot.
- **A repeated stem gives the video a spine.** "We can… We can… We can…", "It's not… It's…",
  "Less X. More Y." The stem is said every time and shown small and pinned (see Styling), while
  the word that changes takes the frame.
- **Opposites and corrections are gifts**: "not X, Y", "before / after", "from X to Y". They
  map straight onto the split, the strike-out and the swap.
- **Punchy and energetic by default**; ~140–170 words a minute. No pauses at the seams.

### When the material is numbers
Results, reports and metrics arrive as a list of figures, and a list of figures cannot be acted
out. Rewrite it; never transcribe it.
- **Lead with what the number did.** "Orders climbed. Fourteen percent." The verb gets the beat
  that acts it out; the figure gets the next one.
- **Three or four figures in the whole video**, at most one per sentence: the ones that carry
  the story. Say them the way people do ("nearly twenty-eight billion"), and let the rest go.
- **Never two figure beats in a row**, and close on words, not on a number.
- **The figure shown is the figure spoken**, digit for digit.

## Graphics and beats
**A graphic covers one sentence or one turn of the idea, 3–8 seconds.** Inside it the idea
runs as **beats**, each landing on its spoken cue, and each beat is one of two kinds:
- **A phrase beat**, the plain beat: a spoken phrase of two to six words, built word by word
  with \`${SKILL_NAMES.wordPopSettle}\` at the headline size. It is set like text, not fitted to
  the frame.
- **A keyword beat**: one word (or a figure and its unit) that fills the frame and acts out
  what it means. **A graphic has at most one, and it is the graphic's key beat.**

A graphic is phrases leading to its key beat: "Your audience isn't reading" as phrases, then
WATCHING filling the frame. A word that would make a good keyword but is not the key beat stays
in its phrase, set in 700.
- **Every graphic is authored, one by one.** Each motion graphics scene gets its own plan and
  its own composition. Never delete a graphic you planned to save work, and never cover
  narration by reusing another graphic's scene: a repeated graphic over different words is a
  defect, and so is narration left to a stock design.
- **Pace: a phrase beat every 0.75–1.5 seconds; the key beat holds up to ~2 seconds.** A beat
  brings a new word or phrase; it is never the last word changing colour.
- **The keyword beat is a technique, chosen for what its word means.** This table is the
  vocabulary; read each sentence of the script against it to find its word before planning:

| The word means… | Technique |
|---|---|
| grow, stretch, rise in size, more, expand (or shrink) | \`${SKILL_NAMES.glyphGrowStretch}\` |
| fall, drop, land, crash, squash, heavy, impact | \`${SKILL_NAMES.glyphDropAndLand}\` |
| burst, pop, surprise, wow, overshoot, excess | \`${SKILL_NAMES.glyphOvershootPop}\` |
| repeat, again, every, momentum, scale, multiply | \`${SKILL_NAMES.wordStaggerEcho}\` |
| opposites, versus, before/after, either/or, two sides | \`${SKILL_NAMES.figureGroundSplit}\` |
| not, no more, without, forget, the old way | \`${SKILL_NAMES.wordStrikeOut}\` |
| "not X but Y" in one slot, a correction | \`${SKILL_NAMES.strikethroughReplace}\` |
| up, down, forward, back, climb, advance, retreat | \`${SKILL_NAMES.wordTravelsDirection}\` |
| loop, cycle, always, around, routine, 24/7 | \`${SKILL_NAMES.orbitTextRing}\` |
| fast, speed, rush, blur, now | \`${SKILL_NAMES.motionBlurStreak}\` (a whip across the frame) |
| a figure, a count, a price | \`${SKILL_NAMES.numberWheelRoll}\` |
| a word that is a thing (a sun, a heart, a key) | \`${SKILL_NAMES.typeAsObject}\` |
| a list of things in one slot ("for X, Y and Z") | \`${SKILL_NAMES.wordSwapInPlace}\` |
| a verdict, a name, the payoff | \`${SKILL_NAMES.headlineSlam}\` |
| the closing answer word | \`${SKILL_NAMES.italicSkewSwing}\` |

- **Every phrase beat is built with \`${SKILL_NAMES.wordPopSettle}\`.** A phrase beat may also
  carry a hero treatment, chosen for what the phrase does, as the graphic's one hero moment
  (see Surfaces):

| The phrase is… | Hero treatment |
|---|---|
| a phrase whose last word is the point ("…a **video**") | \`${SKILL_NAMES.underlineDraw}\`, often with the key-word variant of \`${SKILL_NAMES.textureMaskedType}\` on the word |
| a phrase with a name, a brand, or a key word | the key-word variant of \`${SKILL_NAMES.textureMaskedType}\` |
| the payoff about scale, the whole, everything (once per video) | \`${SKILL_NAMES.extrusionFlyIn}\` |

- **Phrase to phrase on the same ground** carries the motion across the cut with
  \`${SKILL_NAMES.velocityMatchedCut}\`, without its opacity: the outgoing line accelerates off
  sideways and the incoming one pops in displaced the same way and settles.
- **Each graphic has one moment, and the rest of its beats carry the words.** The key beat acts
  out its word (or carries its phrase hero treatment) and wears the graphic's hero moment. Every
  other beat is a phrase on \`${SKILL_NAMES.wordPopSettle}\`, with its word accent (see Styling).
  A second frame-filling word, a second technique or a texture anywhere but the key beat is a
  second moment, and then neither lands.
- **The meaning wins over variety**: two key words that both mean "rise" both rise. Otherwise
  no technique in two graphics in a row.
- **The key beat** of each graphic (its payoff, usually last) may hold up to ~2s; every other
  beat holds about a second.
- **A figure, when it is the key beat, is a punch beat.** It fills the frame in the condensed face, and its unit is a
  line of the justified block (BILLION over EUROS, fitted to the same width), never a small
  label under it. It arrives with \`${SKILL_NAMES.numberWheelRoll}\` or a plain mask rise.

## One continuous piece
Good kinetic typography never shows a seam: the words change, but the video reads as one piece.
- **The stem** (see Styling) is the constant: same place, same size, every graphic.
- **No empty frames, inside a graphic or across a cut.** A graphic opens on its first word and
  ends with a word still on screen.

## Timing: land on the word
- **Fill time with beats, never with holds.** If a word has time to spare before the next, the
  line has another word worth a beat: give it one. One phrase sitting on screen for three
  seconds is the format failing, however well it arrived.
- **A ground is never empty.** From the first frame to the last, a keyword is on screen or
  arriving; a bare ground showing for more than two frames is a flash of nothing. The first word
  of a graphic lands within ~0.1s of its start (the cut is on its cue), and a ground cut happens on
  the incoming word's arrival, never ahead of it.
- **One keyword or phrase readable at a time.** The outgoing beat is gone, or no longer
  readable, by the time the next is: two beats overlapping for more than ~0.15s is a collision,
  not a handoff.
- **In a phrase, each word appears on its own cue**: visible on the frame its sound starts,
  already moving, and settled about 0.4s later. Never a word before it is said.
- **Arrivals land on the cue**: start the incoming move 0.05–0.15s before its word's cue so the
  word is readable as it is heard. A drop's first contact lands on the cue.
- **Departures straddle the next cue**: the outgoing word is gone by the time the next is
  readable. Never an empty frame for more than two frames; never two words readable at once
  outside a deliberate stack (an anchor stem, two stacked opposites, an echo).
- **Position every tween on its word's cue** from the scene's cues, as named constants or a
  table a loop walks. Never an even stagger across beats.
- **Open on motion; land the last beat at least ~1s before the cut.**

## Styling
**The template's fonts lead; the library adds variety.** Call \`${TOOL_NAMES.addFonts}\` without
fonts to see the template's fonts and the font library, then call it again with one or two
library fonts that give the template's faces what they lack for this format: a condensed face
for long words and lines that must fill a landscape frame, a heavy display face for short punchy
words, or a contrast face for the one word set apart. Pick faces that suit the subject and sit
well beside the template's. Add none if the template already has them. The result says which
\`var(--l5-font-N)\` carries each. Kinetic typography lives on colour contrast:
- **Five grounds** from the template's palette: a dark, a light, a hot accent, a second ground,
  and a still gradient in the brand's colours (\`.ds-ground-gradient\`: two or three soft radial
  blooms of the accent and its neighbours over the second ground), plus
  \`${SKILL_NAMES.driftingMeshGround}\` for at most one key beat in three.
- **The ground changes with the phrase, never with the word.** A phrase may open on a new
  ground, a hard cut with its first word, and that ground holds for the whole phrase, at least
  ~1.2 seconds. About every other phrase changes ground, never two short beats in a row, and
  the key beat always cuts to its own. Never cut inside a phrase, and never cut back and forth
  between two grounds on consecutive beats: that is flashing. The type flips to whichever of ink
  or paper reads on that ground.
- **Keywords: huge type, filling the frame.** Every technique's "Setting the word" applies: the
  word spans 80–90% of the frame's width, in capitals, sized by font-size and marked
  \`data-l5-allow-literal\`. Set long words in the condensed face.
- **Phrases: set like text.** A phrase beat is one centred line at \`--ds-type-headline\`, about
  12% of the frame's height, in sentence case exactly as said, never fitted to the width.
  Weight 500, in the template's first font.
- **A word accent in most phrases.** One word of the phrase is set apart: usually its last
  word, or the word the phrase is about ("You give it a **brief**", "it gives you back a
  **video**", "the new AI teammate **from Lumen5**"). It takes one accent from the design
  system's set, applied as the word pops in:
  - \`.ds-word-accent\`: the word in 700 in the accent colour, or on the accent ground in the
    second ground's colour.
  - \`.ds-word-underline\`: an accent rule under the word, drawn as
    \`background: linear-gradient(ACCENT, ACCENT) 0 95% / var(--u, 0%) 0.08em no-repeat\`, with
    \`--u\` tweened to 100% over 0.3s from the word's cue.
  - \`.ds-word-gradient\`: the word filled with the brand gradient
    (\`background-clip: text; color: transparent\`), often italic in the contrast face.
  Vary the accent from phrase to phrase, and leave a phrase plain now and then. One word per
  phrase; never the whole phrase, and the accent must still read on its ground. These are part
  of the phrase, not techniques: name them in the plan as "accent: colour on 'brief'", and never
  load a technique for them.
- **Type that varies.** Change the key word's face from graphic to graphic: a wide or heavy face
  for short punchy words, a condensed one for long words and stacked lines, a contrast face for
  the one word set apart. Phrases stay in one face.
  Set the stem in the template's first font.
- **The anchor stem**: a repeated stem ("We can", "It's") is set small (\`--ds-type-headline\`),
  pinned top-left inside the margins, identical in every graphic that uses it, its colour
  flipping with the ground. It rises in once with \`${SKILL_NAMES.letterMaskRise}\` and then
  stays: it is the one element allowed to persist across beats and graphics.
- **The hot accent** is for a strike-out bar and one line, one word or one letter of a block at
  most, never a whole sentence; it is also one of the five grounds.
- **Music**: a track with a hard, driving pulse, kept under the voice.

### Surfaces: the key beat looks different
Flat grounds and flat ink on every beat look the same from one video to the next, and the same
texture on every beat is only a busier flat. **Ordinary beats take no texture and no type
treatment, only their word accent, and the key beat of each graphic changes surface**: its ground gets a texture, and its word gets a type treatment.
Since the surface changes only on the payoff, the payoff stands out.

**One hero moment per graphic, in all.** It is either the key beat's texture and treatment, or one
phrase hero treatment from the phrase table, never both. A graphic whose hero is a phrase
treatment takes no texture and no type treatment; the rotation below carries on at the next
graphic that uses them.

In \`${TOOL_NAMES.writeDesignSystem}\`, choose from the lists below, for the subject, **two or
three textures** and **two or three treatments**. Write each as a class in its \`css\`
(\`.ds-ground-dots\`, \`.ds-type-extrude\`) and name them in \`look\`. Graphics use only those
classes and never invent their own.
- **Rotate them across graphics.** Each graphic whose hero is its key beat's surface takes the
  next texture and the next treatment in turn, so no two graphics in a row share either, and the pairs shift (dots with
  extrude, then stripes with colour wipe, then dots with highlighter).
- **One texture at a time, on the key beat only.** Never two textures layered, and never a texture
  under an ordinary beat. It goes on with the key beat's ground cut and comes off with the next
  cut.

**Everything moves at the pace of the beat.** A key beat holds two seconds at most, so a slow
drift is never seen. A texture is still, or it changes on a cut or a cue. A treatment's move
finishes within ~0.4s of its cue.

**Ground textures.** The grounds themselves stay still. A texture is a
full-frame layer just above the ground, hidden until the key beat, drawn in \`currentColor\` at
low strength. Show it and set its \`color\` to the beat's ink in the same \`tl.set\` as the key
beat's ground cut, and hide it in the next cut. Below, \`INK-n\` stands for
\`color-mix(in oklab, currentColor n%, transparent)\`.
| Texture | CSS | Moves |
|---|---|---|
| Dot grid | \`radial-gradient(circle, INK-24 5px, transparent 6px) 0 0 / 60px 60px\` | still |
| Graph paper | two \`linear-gradient(INK-24 2px, transparent 2px)\` at 240px (one rotated 90deg) over two at INK-11 and 48px | still |
| Diagonal stripes | \`repeating-linear-gradient(-45deg, transparent 0 30px, INK-8 30px 60px)\` | still |
| Vignette | \`radial-gradient(ellipse 70% 75% at 50% 50%, transparent 50%, rgb(0 0 0 / .35) 100%)\` | still |
| Mesh | \`radial-gradient(at 15% 20%, rgb(255 255 255 / .18), transparent 55%)\` plus one accent bloom at 45% in the opposite corner | the blooms jump to new corners on a ground cut |
| Spotlight | \`radial-gradient(circle at var(--sx) var(--sy), rgb(255 255 255 / .22), transparent 40%)\` | jumps to a new position on each ground cut (\`tl.set\` on \`--sx\`/\`--sy\`), never drifts |
| Poster mat | \`box-shadow: inset 0 0 0 60px var(--ds-accent)\`, plus a 4px INK-45 hairline frame inset 92px | still; on the accent ground the mat takes the dark |
| Accent slab | a band in the accent behind the key line, rotated -4°, the line's height plus ~20%, centred on that line (measure the line; never centre it on the frame) | wipes in on \`scaleX\` from its left edge in ~0.3s on the key beat's cue |

**Key-beat type treatments.** Ordinary beats wear none of these. The key beat of each graphic (or one
of its lines) wears that graphic's treatment, on top of its technique: "drop" still drops, it
just lands as an extruded block. Set the word as usual first (fitted, capitals); the treatment only
changes how it is painted. \`ACCENT\` is \`var(--ds-accent)\`; \`INK\` is the beat's ink.
| Treatment | Build | On the cue |
|---|---|---|
| Colour wipe | \`background: linear-gradient(90deg, ACCENT 50%, INK 50%) 100% 0 / 200% 100%; background-clip: text; color: transparent\` | tween \`backgroundPosition\` to \`0% 0\`, 0.35s |
| Outline to fill | \`-webkit-text-stroke: 0.022em INK; color: transparent; background: linear-gradient(0deg, INK 50%, transparent 50%) 0 0 / 100% 200%; background-clip: text\` | tween \`backgroundPosition\` to \`0 100%\`, 0.4s. For fitted HTML type, use this rather than \`${SKILL_NAMES.outlineToFillType}\`'s SVG strokes |
| Echo stack | two copies behind the word as its \`::before\` and \`::after\` (\`content: attr(data-text)\`, \`position: absolute; inset: 0; z-index: -1\`, \`color: transparent\`, \`-webkit-text-stroke: 0.014em INK\`, opacity .7 and .4), offset by \`translate(calc(var(--echo) * 0.05em), calc(var(--echo) * 0.06em))\` and twice that. Never copies in the markup: the scale check would read them as a second word | tween \`--echo\` on the word from 0 to 1, 0.3s. Not \`${SKILL_NAMES.wordStaggerEcho}\`, which stacks rows |
| Extruded block | \`text-shadow\` of 12 steps at \`calc(var(--d) * n)\` in the accent darkened 30%, built in a loop | tween \`--d\` from 0 to 0.008em while the word lifts by (-0.048em, -0.048em), 0.3s \`back.out\` |
| Long shadow | \`text-shadow\` of 40 steps of 0.008em at 45°, from the ground darkened 30% fading to the ground, built in a loop | arrives with the word; still |
| Highlighter | \`${SKILL_NAMES.inlineHighlightChip}\` on one word of the block | as that skill does it |
| Difference blend | the word in \`#fff\` with \`mix-blend-mode: difference\`; an accent disc ~35% of the frame wide behind it | the disc sweeps across the word in ~0.6s, inverting the letters as it passes |
| Glass pill | the word fitted to 74% of the frame's width inside a pill: \`border-radius: 999px; padding: 0.1em 0.3em\`, the ground at 30% opacity, a 2px INK-35 border, \`backdrop-filter: blur(56px) saturate(1.4)\` | two discs (accent and a lighter tint) behind the pill jump to new places on the cue; the frost needs something behind it |

No film grain (it vanishes on flat colour), letterpress (hard to read), conic rays or
scanlines.

## Build order
1. \`${TOOL_NAMES.loadSkill}\` \`${SKILL_NAMES.motionGraphicsScenesBasics}\`, then
   \`${SKILL_NAMES.htmlComposition}\`, then \`${SKILL_NAMES.easingCurves}\`, then
   \`${SKILL_NAMES.actOutTheLine}\`, then \`${SKILL_NAMES.wordPopSettle}\`, **before anything
   else**, the script included.
2. One \`${TOOL_NAMES.addVoiceoverScene}\` call with every word range, then
   \`${TOOL_NAMES.addFonts}\`.
3. \`${TOOL_NAMES.writeDesignSystem}\` **once, before the first plan**: the grounds, the ink
   and paper, the accent, the stem's size, the gradient ground, the three word accents, and the
   look's two or three texture and treatment classes.
4. **Plan every graphic in one \`${TOOL_NAMES.writePlan}\` call**, a \`plans\` entry per scene in
   play order, so their techniques are chosen together.
5. Then build the graphics **one at a time, in play order**: load techniques, author, then
   \`${TOOL_NAMES.reviewSceneImages}\` on that graphic before starting the next, with \`seconds\`
   set **beat by beat**: just after every cue, halfway between consecutive cues, and on the
   graphic's first and last frames. A frame with no word, two words, a word cut by the frame
   edge, a word with jumbled letters, or a figure that has settled on other digits than the ones
   spoken is a defect to fix before moving on; the reviewer's default frames fall between beats
   and miss all of them.
6. **Build every graphic before you reply.** A video with graphics still unbuilt is not a draft
   to hand over; carry on to the last one, then review the whole video.

Fill each plan as a beat sheet:
- \`text\`: every keyword and phrase, exactly as shown, plus the stem if this graphic shows it.
- \`graphics\`: empty. Type is not a drawn object; listing it as a graphic pulls in the drawing
  method for nothing.
- \`layout\`: "one beat at a time: phrases centred at the headline size, then the key word
  filling the frame" (plus "stem pinned top-left").
- \`phases\`: **one per beat, about one per second of graphic** (a 4-second graphic has 3–5),
  **every one with an \`enters\`**, \`at\` on its word's cue, and the
  **meaning and its technique** named ("'You give it a brief': phrase, word pop settle, accent:
  colour on 'brief', ground cuts to the gradient",
  "'grow': the letters stretch up past their height, glyph grow & stretch; the ground cuts to the
  accent"). \`exits\` is the word before. The key beat's phase also names the graphic's one hero
  moment: its texture and treatment, the next in turn, or its phrase hero treatment.
- **Count each plan before you send it**: one key beat, and every other beat a phrase with at
  most a word accent and a ground change. No other word fills the frame, no other beat names a
  technique from the table, and no ground holds for less than a whole phrase. Planned all at once, before any graphic is built, every
  beat tends to get something; the plain phrases are what let the key beat land.

\`${TOOL_NAMES.writePlan}\` assigns one technique **per beat** from the vocabulary above, placed on
its phase. When you build a graphic, **load every technique its plan names in one turn** — skip
one already loaded above — and implement them all, each on the beat it is placed on, as its skill
implements it. A beat whose
technique was assigned but which arrives as a fade is the format failing.

## Where this overrides the standard build
These win over \`${SKILL_NAMES.createVideo}\`, over \`${SKILL_NAMES.htmlComposition}\`'s style
guidance, and over your standing build instructions.
- **Pacing**: ignore the ~3-4 second cap on anything on screen and "a full sentence needs two or
  more overlays". The graphic is the scene; its beats are the pace.
- **Media, designs, charts**: no stock or user media, no template designs, no chart asset.
- **Phases are beats.** Ignore "one phase per five seconds". A five-second graphic has four to
  six.
- **The type scale**: frame-filling keywords are sized to the frame, not from
  \`--ds-type-hero\`. \`design-token-bypass\` on a keyword marked \`data-l5-allow-literal\` is this
  format; on a phrase or the stem it is real.
- **Ground changes**: a hard cut of the ground with a phrase or the key beat is allowed and
  expected here.
- **The look**: its textures, slab, discs and pill may share the frame with the type; "nothing
  else on the frame" in each technique's "Setting the word" means nothing beyond them.
- **Fonts**: use the template's faces and the ones you added, the key word's face changing
  between graphics; "as few
  fonts as the design needs" does not apply.
- **On-screen text is the keyword, or the spoken phrase of two to six words**, plus the stem.
- **Lint warnings that are this format working**: \`text-reading-time\` on a keyword up ~0.5s or
  longer, or a phrase up ~0.75s or longer, while it is spoken; \`text-overlap\` during a handoff
  under ~0.15s.
- **Lint warnings that are real**: \`text-out-of-time-with-narration\` (retime to the cue),
  \`motion-dead-stretch\` (a beat overstays), \`text-clipped-by-container\` on a word at rest.

## Review
Load \`${SKILL_NAMES.reviewVideo}\`. You have reviewed each graphic as you built it; this pass is
about the whole video:
- **Coverage**: every transcript word is covered and captions are off.
- **Sync**: in the review frames, each keyword is the one being said or just said.
- **Meaning**: read each graphic's beats back from its plan. Its key beat acts out its word
  with a technique from the table that fits the word; every other beat is a phrase.
- **Phrases**: every phrase builds word by word with no fades, and most set one word apart with
  an accent that reads on its ground.
- **Scale and colour**: each graphic's one keyword fills most of the frame's width, and every
  phrase is at the headline size; every ground holds a whole phrase, with no flashing; the stem,
  if any, is in the same place in every frame.
- **Look**: each graphic has one hero moment, either a key beat's texture and treatment (one of
  each, from the design system's set) or a phrase hero treatment, never both; no two graphics in a row share a texture or a treatment; and every word is
  readable on them. Contrast is not checked for outlined,
  colour-wiped or blended type, so check those beats in the review frames yourself.
- **Figures**: each figure at rest is the one spoken, digit for digit.
- **Pace**: phrase beats every 0.75–1.5s, nothing holding
  longer except a graphic's key beat, and
  every graphic's last beat lands a second before its cut.`,
};
